// src/components/das-application/PDFPreviewModal.js
import React, { memo, useEffect, useState, useContext } from "react";
import { NotificationContext } from "../../context/NotificationContext";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import { lazyLoadReactPDF } from "../utils/lazyLoadReactPDF"; // 🟢 Use your lazy loader

import ApplicationPDF from "./ApplicationPDF";
import {
  useGetApplicationDataQuery,
  useSendPDFMutation,
} from "../../services/das-app";
import SendPDFDialog from "./SendPDFDialog";

const getPDF = async () => {
  const { pdf } = await import("@react-pdf/renderer");
  return pdf;
};
const PDFViewer = lazyLoadReactPDF("PDFViewer");
const PDFDownloadLink = lazyLoadReactPDF("PDFDownloadLink");

function PDFPreviewModal({ open, onClose, appID }) {
  const [pdfData, setPdfData] = useState(null);
  const { addNotification } = useContext(NotificationContext);
  const { data, isLoading, isError, error } = useGetApplicationDataQuery(
    appID,
    {
      skip: !open || !appID,
    }
  );
  // console.log("data:", data);

  const [sendPDF, { isLoading: isSendingPDF }] = useSendPDFMutation();
  const [generatedPDFBlob, setGeneratedPDFBlob] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    if (data && data.success) {
      setPdfData(data?.data);
    }
  }, [data]);
  // console.log("pdfData:", pdfData);

  useEffect(() => {
    if (pdfData && open) {
      const generateBlob = async () => {
        try {
          const blob = await (
            await getPDF()
          )(<ApplicationPDF data={pdfData} />).toBlob();
          setGeneratedPDFBlob(blob);
        } catch (err) {
          console.error("Error generating PDF blob:", err);
          addNotification("Failed to generate PDF for emailing.", "error");
        }
      };
      generateBlob();
    }
  }, [pdfData, open, addNotification]);

  const handleSendEmail = async () => {
    if (!generatedPDFBlob) {
      addNotification("PDF is not ready to be sent.", "error");
      return;
    }

    try {
      const pdfFileName =
        `${pdfData?.step4.school}_${pdfData?.step1.last_name}_${pdfData?.step1.first_name}_Application.pdf` ||
        "Application.pdf";
      const pdfFile = new File([generatedPDFBlob], pdfFileName, {
        type: "application/pdf",
      });

      const formData = new FormData();
      formData.append("pdf", pdfFile, pdfFileName);
      formData.append("email", email);

      await sendPDF({ appID, formData }).unwrap();

      addNotification(
        "PDF has been sent to your email successfully.",
        "success"
      );
    } catch (err) {
      console.error("Error sending PDF via email:", err);
      addNotification("Failed to send PDF via email.", "error");
    }
  };

  const handleOpenConfirmDialog = () => {
    setEmail("");
    setEmailError("");
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError("Please enter a valid email address.");
    } else {
      setEmailError("");
    }
  };

  const handleConfirmSendEmail = async () => {
    if (!email) {
      setEmailError("Email is required.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    await handleSendEmail(email);
    setOpenConfirmDialog(false);
  };

  const renderPDF = () => {
    if (isLoading) {
      return (
        <div style={{ textAlign: "center" }}>
          <CircularProgress />
        </div>
      );
    }

    if (isError) {
      return (
        <p>Error loading PDF data: {error?.data?.message || error.error}</p>
      );
    }

    if (pdfData) {
      return (
        <PDFViewer style={{ width: "100%", height: "100%", border: "none" }}>
          <ApplicationPDF data={pdfData} />
        </PDFViewer>
      );
    }

    return <p>Unable to load PDF.</p>;
  };

  const pdfFileName =
    `${pdfData?.step4.school}_${pdfData?.step1.last_name}_${pdfData?.step1.first_name}_Application.pdf` ||
    "Application.pdf";

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth className="pdf-dialog">
        <DialogTitle>Application PDF Preview</DialogTitle>
        <DialogContent dividers sx={{ width: "100%", overflow: "hidden" }}>
          {renderPDF()}
        </DialogContent>
        <DialogActions>
          {pdfData && (
            <>
              <PDFDownloadLink
                document={<ApplicationPDF data={pdfData} />}
                fileName={pdfFileName}
                style={{
                  textDecoration: "none",
                  padding: "10px 20px",
                  color: "#fff",
                  backgroundColor: "#5DC1AF",
                  borderRadius: "4px",
                }}
              >
                {({ blob, url, loading, error }) =>
                  loading ? "Preparing document..." : "Download PDF"
                }
              </PDFDownloadLink>
              <Button
                onClick={handleOpenConfirmDialog}
                variant="contained"
                color="primary"
                disabled={isSendingPDF || !generatedPDFBlob}
                startIcon={isSendingPDF && <CircularProgress size={20} />}
              >
                {isSendingPDF ? "Sending..." : "Send PDF via Email"}
              </Button>
            </>
          )}
          <Button onClick={onClose} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <SendPDFDialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        email={email}
        handleEmailChange={handleEmailChange}
        emailError={emailError}
        handleConfirmSendEmail={handleConfirmSendEmail}
        isLoading={isSendingPDF}
      />
    </>
  );
}

export default memo(PDFPreviewModal);
